/**
 * Structured API Error
 *
 * Provides a consistent error structure for all API errors
 */
export interface ApiError {
    /** HTTP status code (0 for network errors) */
    status: number;
    /** Error message suitable for display to users */
    message: string;
    /** Original error message from server (for debugging) */
    originalMessage?: string;
    /** Validation errors keyed by field name */
    fieldErrors?: Record<string, string[]>;
    /** Whether this is a network/connectivity error */
    isNetworkError: boolean;
    /** Whether user should be redirected (e.g., after 401) */
    shouldRedirect: boolean;
    /** Where to redirect (if shouldRedirect is true) */
    redirectTo?: string;
}

/**
 * User-friendly error messages for different scenarios
 */
export const ERROR_MESSAGES = {
    NETWORK_ERROR: 'We couldn\'t connect. Please check your internet connection and try again.',
    TIMEOUT_ERROR: 'This is taking longer than expected. Please try again.',
    SERVER_ERROR: 'We hit a problem on our side. Please try again in a moment.',
    SESSION_EXPIRED: 'Your session has expired. Please log in again.',
    ACCESS_DENIED: 'You don\'t have permission to perform this action.',
    NOT_FOUND: 'The requested resource was not found.',
    VALIDATION_ERROR: 'Please check your input and try again.',
    UNKNOWN_ERROR: 'Something unexpected happened. Please try again.',
} as const;

/**
 * Check if an error is an ApiError
 */
export function isApiError(error: unknown): error is ApiError {
    return (
        typeof error === 'object' &&
        error !== null &&
        'status' in error &&
        'message' in error &&
        'isNetworkError' in error
    );
}

/**
 * Extract user-friendly message from any error
 */
export function getErrorMessage(error: unknown): string {
    if (isApiError(error)) {
        return error.message;
    }
    if (error instanceof Error) {
        return error.message;
    }
    return ERROR_MESSAGES.UNKNOWN_ERROR;
}
